import { ORDER_STATUSES } from "@/modules/orders/domain/contracts";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
    findCondition,
    removeConditions,
    upsertCondition,
} from "@/shared/contracts/query-helpers";
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ",
  cancelled: "ຍົກເລີກ",
};

function buildFilters(
  current: FilterConditionDTO[],
  updates: {
    orderCode?: string;
    status?: string;
    channel?: string;
    dateFrom?: string;
    dateTo?: string;
  },
): FilterConditionDTO[] {
  let next = removeConditions(current, "code") ?? [];
  next = removeConditions(next, "status") ?? [];
  next = removeConditions(next, "channel") ?? [];
  next = removeConditions(next, "createdAtGte") ?? [];
  next = removeConditions(next, "createdAtLte") ?? [];
  if (updates.orderCode) {
    next = upsertCondition(next, {
      field: "code",
      op: "contains",
      value: updates.orderCode,
    });
  }
  if (updates.status) {
    next = upsertCondition(next, {
      field: "status",
      op: "eq",
      value: updates.status,
    });
  }
  if (updates.channel) {
    next = upsertCondition(next, {
      field: "channel",
      op: "eq",
      value: updates.channel,
    });
  }
  if (updates.dateFrom) {
    next = upsertCondition(next, {
      field: "createdAtGte",
      op: "gte",
      value: updates.dateFrom + "T00:00:00.000Z",
    });
  }
  if (updates.dateTo) {
    next = upsertCondition(next, {
      field: "createdAtLte",
      op: "lte",
      value: updates.dateTo + "T23:59:59.999Z",
    });
  }
  return next;
}

export function OrdersFilter() {
  const nav = useNavigate({ from: "/app/orders" });
  const search = useSearch({ from: "/app/orders" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const codeFilter = findCondition(filters, "code");
  const statusFilter = findCondition(filters, "status");
  const channelFilter = findCondition(filters, "channel");
  const createdAtGte = findCondition(filters, "createdAtGte");
  const createdAtLte = findCondition(filters, "createdAtLte");

  const [orderCodeValue, setOrderCodeValue] = useState<string>(
    (codeFilter?.value as string) ?? "",
  );
  useEffect(() => {
    setOrderCodeValue((codeFilter?.value as string) ?? "");
  }, [codeFilter?.value]);

  const statusValue = (statusFilter?.value as string) ?? "";
  const channelValue = (channelFilter?.value as string) ?? "";
  const dateFrom = (createdAtGte?.value as string)?.slice(0, 10) ?? "";
  const dateTo = (createdAtLte?.value as string)?.slice(0, 10) ?? "";

  const debouncedOrderCode = useDebounceCallback((val: string) => {
    setOrderCodeValue(val);
    const next = buildFilters(filters, {
      orderCode: val || undefined,
      status: statusValue || undefined,
      channel: channelValue || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  }, 400);

  const handleStatusChange = (value: string) => {
    const next = buildFilters(filters, {
      orderCode: orderCodeValue || undefined,
      status: value === "all" ? undefined : value,
      channel: channelValue || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleDateFromChange = (value: string) => {
    const next = buildFilters(filters, {
      orderCode: orderCodeValue || undefined,
      status: statusValue || undefined,
      channel: channelValue || undefined,
      dateFrom: value || undefined,
      dateTo: dateTo || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const handleDateToChange = (value: string) => {
    const next = buildFilters(filters, {
      orderCode: orderCodeValue || undefined,
      status: statusValue || undefined,
      channel: channelValue || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: value || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const clearFilters = () => {
    nav({
      search: { ...search, offset: 0, filters: undefined } as Record<
        string,
        unknown
      >,
    });
  };

  const handleChannelChange = (value: string) => {
    const next = buildFilters(filters, {
      orderCode: orderCodeValue || undefined,
      status: statusValue || undefined,
      channel: value === "all" ? undefined : value,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next.length ? next : undefined,
      } as Record<string, unknown>,
    });
  };

  const hasFilter = Boolean(
    codeFilter?.value ||
      statusFilter?.value ||
      channelFilter?.value ||
      createdAtGte?.value ||
      createdAtLte?.value,
  );

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-4 px-2"
      data-tourid="orders-filter"
    >
      <Input
        placeholder="ຄົ້ນຫາເລກຄຳສັ່ງ (ORD-...)"
        defaultValue={orderCodeValue}
        onChange={(e) => {
          const v = e.target.value;
          debouncedOrderCode(v);
        }}
        className="h-8 sm:max-w-2xs"
      />
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-muted-foreground text-sm">
          ວັນທີ (ຈາກ)
        </span>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => handleDateFromChange(e.target.value)}
          className="h-8 w-40"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-muted-foreground text-sm">
          ວັນທີ (ຖິ່ນ)
        </span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => handleDateToChange(e.target.value)}
          className="h-8 w-40"
        />
      </div>
      <Select value={statusValue || "all"} onValueChange={handleStatusChange}>
        <SelectTrigger className="h-8 w-[160px]">
          <SelectValue placeholder="ສະຖານະ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທັງໝົດ</SelectItem>
          {ORDER_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {STATUS_LABELS[s] ?? s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={channelValue || "all"} onValueChange={handleChannelChange}>
        <SelectTrigger className="h-8 w-[140px]">
          <SelectValue placeholder="ຊ່ອງທາງ" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">ທຸກຊ່ອງທາງ</SelectItem>
          <SelectItem value="web">ເວັບ</SelectItem>
          <SelectItem value="retail">ຫນ້າຮ້ານ</SelectItem>
          <SelectItem value="social">Social Media</SelectItem>
          <SelectItem value="pos">POS (ເກົ່າ)</SelectItem>
        </SelectContent>
      </Select>
      {hasFilter && (
        <Button variant="ghost" size="sm" onClick={clearFilters}>
          <XIcon className="h-4 w-4" /> ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
